import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, Search, Pencil, Trash2, GraduationCap, Key, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import SetPasswordDialog from '../../components/SetPasswordDialog';
import ReportCard from '../../components/ReportCard';

const defaultForm = { first_name: '', last_name: '', email: '', date_of_birth: '', gender: '', division: '', grade: '', class_id: '', medical_info: '', address: '' };

export default function StudentManagement() {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [search, setSearch] = useState('');
  const [filterDiv, setFilterDiv] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editing, setEditing] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [reportStudent, setReportStudent] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (filterDiv) params.set('division', filterDiv);
    api.get(`/admin/students?${params}`).then(r => setStudents(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, [search, filterDiv]);
  useEffect(() => {
    api.get('/admin/classes').then(r => setClasses(r.data)).catch(() => {});
    api.get('/admin/divisions').then(r => setDivisions(r.data)).catch(() => {});
  }, []);

  const grades = divisions.find(d => d.name === form.division)?.grades || [];

  const handleSave = async () => {
    try {
      const payload = { ...form, class_id: form.class_id === 'none' ? '' : form.class_id };
      if (editing) {
        await api.put(`/admin/students/${editing}`, payload);
        toast.success('Student updated');
      } else {
        await api.post('/admin/students', payload);
        toast.success('Student created');
      }
      setShowDialog(false);
      setForm(defaultForm);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error saving student');
    }
  };

  const handleEdit = (s) => {
    setForm({ first_name: s.first_name, last_name: s.last_name, email: s.email, date_of_birth: s.date_of_birth || '', gender: s.gender || '', division: s.division || '', grade: s.grade || '', class_id: s.class_id || '', medical_info: s.medical_info || '', address: s.address || '' });
    setEditing(s.id);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this student?')) return;
    await api.delete(`/admin/students/${id}`);
    toast.success('Student deactivated');
    load();
  };

  return (
    <div data-testid="student-management">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1B2A4A] flex items-center gap-2"><GraduationCap className="w-6 h-6" /> Students</h1>
          <p className="text-sm text-slate-500">{students.length} active students</p>
        </div>
        <Button onClick={() => { setForm(defaultForm); setEditing(null); setShowDialog(true); }} className="bg-[#1B2A4A] hover:bg-[#283E6B]" data-testid="add-student-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Student
        </Button>
        <Button variant="outline" className="border-[#1B2A4A] text-[#1B2A4A]" onClick={() => { const token = localStorage.getItem('token'); fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/students/export`, { headers: { Authorization: `Bearer ${token}` } }).then(r=>r.blob()).then(b=>{ const a=document.createElement('a'); a.href=URL.createObjectURL(b); a.download='students_export.csv'; a.click(); }); }} data-testid="export-students-csv">
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search students..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="search-students-input" />
        </div>
        <Select value={filterDiv} onValueChange={v => setFilterDiv(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-48" data-testid="filter-division-select"><SelectValue placeholder="All Divisions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Divisions</SelectItem>
            {divisions.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="data-table">
              <TableHead>Name</TableHead>
              <TableHead>Admission No.</TableHead>
              <TableHead>Division</TableHead>
              <TableHead>Grade</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {students.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No students found</TableCell></TableRow>
            ) : students.map(s => (
              <TableRow key={s.id}>
                <TableCell className="font-semibold text-[#1B2A4A]">{s.first_name} {s.last_name}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-xs">{s.admission_number}</Badge></TableCell>
                <TableCell>{s.division}</TableCell>
                <TableCell>{s.grade}</TableCell>
                <TableCell className="text-slate-500 text-sm">{s.email}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" title="Report Card" onClick={() => setReportStudent(s.id)} data-testid={`report-card-${s.id}`}><FileText className="w-4 h-4 text-violet-600" /></Button>
                  <Button variant="ghost" size="icon" title="Set Password" onClick={() => setPwUser({ id: s.user_id, name: `${s.first_name} ${s.last_name}`, email: s.email })} data-testid={`set-password-student-${s.id}`}><Key className="w-4 h-4 text-amber-600" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(s)} data-testid={`edit-student-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(s.id)} data-testid={`delete-student-${s.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Student' : 'Add Student'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>First Name</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} data-testid="student-first-name" /></div>
            <div><Label>Last Name</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} data-testid="student-last-name" /></div>
            <div className="col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="student-email" /></div>
            <div><Label>Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={e => setForm({...form, date_of_birth: e.target.value})} /></div>
            <div>
              <Label>Gender</Label>
              <Select value={form.gender} onValueChange={v => setForm({...form, gender: v})}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Division</Label>
              <Select value={form.division} onValueChange={v => setForm({...form, division: v, grade: ''})}>
                <SelectTrigger><SelectValue placeholder="Select Division" /></SelectTrigger>
                <SelectContent>{divisions.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Grade</Label>
              <Select value={form.grade} onValueChange={v => setForm({...form, grade: v})}>
                <SelectTrigger><SelectValue placeholder="Select Grade" /></SelectTrigger>
                <SelectContent>{grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Class</Label>
              <Select value={form.class_id || 'none'} onValueChange={v => setForm({...form, class_id: v === 'none' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="Select Class (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {classes.filter(c => !form.division || c.division === form.division).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#1B2A4A]" data-testid="save-student-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Password Dialog */}
      {pwUser && <SetPasswordDialog open={!!pwUser} onOpenChange={() => setPwUser(null)} userId={pwUser.id} userName={pwUser.name} userEmail={pwUser.email} />}

      {/* Report Card Dialog */}
      <Dialog open={!!reportStudent} onOpenChange={() => setReportStudent(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="sr-only"><DialogTitle>Report Card</DialogTitle></DialogHeader>
          {reportStudent && <ReportCard studentId={reportStudent} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
